import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { AttendanceLogApiService } from '../../features/attendance-log/services/attendance-log-api.service';
import { downloadBlob } from '../../shared/utils/download-blob';
import { GENERIC_USER_ERROR } from '../master-schedule/master-schedule-user-errors';
import { ToastActions } from '../toast/toast.actions';
import { AttendanceLogActions } from './attendance-log.actions';

export const NOTHING_TO_EXPORT_MESSAGE = 'Nothing to export for these filters.';

@Injectable()
export class AttendanceLogEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(AttendanceLogApiService);

  loadCrewLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttendanceLogActions.loadCrewLog),
      switchMap(({ query }) =>
        this.api.getCrewLog(query).pipe(
          map((result) => AttendanceLogActions.loadCrewLogSuccess({ result })),
          catchError((error) => {
            console.error('Crew log failed to load', error);
            return of(AttendanceLogActions.loadCrewLogFailure({ error: GENERIC_USER_ERROR }));
          })
        )
      )
    )
  );

  exportCrewLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttendanceLogActions.exportCrewLog),
      exhaustMap(({ query, fileName }) =>
        this.api.exportCrewLog(query).pipe(
          map((blob) => AttendanceLogActions.exportCrewLogSuccess({ blob, fileName })),
          catchError((error) => {
            console.error('Crew log export failed', error);
            const empty = error instanceof HttpErrorResponse && error.status === 404;
            return of(
              AttendanceLogActions.exportCrewLogFailure({
                reason: empty ? 'empty' : 'error',
                message: empty ? NOTHING_TO_EXPORT_MESSAGE : GENERIC_USER_ERROR,
              })
            );
          })
        )
      )
    )
  );

  downloadExport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AttendanceLogActions.exportCrewLogSuccess),
        tap(({ blob, fileName }) => downloadBlob(blob, fileName))
      ),
    { dispatch: false }
  );

  exportFailureToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttendanceLogActions.exportCrewLogFailure),
      map(({ reason, message }) =>
        ToastActions.showToast({
          severity: reason === 'empty' ? 'info' : 'error',
          summary: 'Export Log',
          detail: message,
        })
      )
    )
  );
}
